import React, { useRef } from 'react';
import { Field } from 'react-final-form';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { richText } from '../../util/richText';
import { ValidationError } from '../../components';

// Category mode reuses the look of the standard controls (D17, D39) without changing them
import checkboxCss from '../FieldCheckbox/FieldCheckbox.module.css';
import radioCss from '../FieldRadioButton/FieldRadioButton.module.css';
import css from './FieldSelectTree.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 16;

/**
 * Collects all focusable option buttons in the tree in DFS order.
 *
 * @param {HTMLElement} container - The container element containing the option buttons
 * @returns {Array<HTMLElement>} Array of focusable option buttons in order
 */
const getAllFocusableOptions = container => {
  if (!container) return [];
  const buttons = container.querySelectorAll('button');
  return Array.from(buttons).filter(button => {
    // Filter out disabled buttons and buttons with negative tabIndex
    return !button.disabled && button.tabIndex !== -1;
  });
};

/**
 * Finds the parent <ul> element that contains the given button.
 *
 * @param {HTMLElement} button - The button element
 * @returns {HTMLElement|null} The parent <ul> element or null if not found
 */
const getParentOptionList = (button, container) => {
  let parent = button.parentElement;
  while (parent && parent.tagName !== 'UL') {
    parent = parent.parentElement;
  }
  if (parent === container.firstChild) {
    parent = parent.closest('form');
  }
  return parent;
};

/**
 * Finds the parent option button (the button in the parent <li> that contains the current button's <ul>).
 *
 * @param {HTMLElement} button - The currently focused button
 * @param {HTMLElement} container - The container element containing all options
 * @returns {HTMLElement|null} The parent option button or null if not found
 */
const getParentOptionButton = (button, container) => {
  const parentList = getParentOptionList(button, container);
  if (!parentList) return null;

  // Find the parent <li> that contains this <ul>
  const parentLi = parentList.parentElement;
  if (!parentLi || parentLi.tagName !== 'LI') return null;

  // Find the button within the parent <li>
  const parentButton = parentLi.querySelector('button');
  return parentButton || null;
};

/**
 * Finds the next focusable option button within the same parent list, with looping.
 *
 * @param {HTMLElement} currentButton - The currently focused button
 * @param {HTMLElement} container - The container element containing all options
 * @returns {HTMLElement|null} The next focusable button or null if none exists
 */
const getNextOption = (currentButton, container) => {
  const parentList = getParentOptionList(currentButton, container);
  if (!parentList) return null;

  const siblingButtons = Array.from(parentList.querySelectorAll('button')).filter(
    button => !button.disabled && button.tabIndex !== -1
  );
  const currentIndex = siblingButtons.indexOf(currentButton);
  if (currentIndex === -1) return null;

  // Loop to first if at the end
  const nextIndex = currentIndex === siblingButtons.length - 1 ? 0 : currentIndex + 1;
  return siblingButtons[nextIndex];
};

/**
 * Finds the previous focusable option button within the same parent list, with looping.
 *
 * @param {HTMLElement} currentButton - The currently focused button
 * @param {HTMLElement} container - The container element containing all options
 * @returns {HTMLElement|null} The previous focusable button or null if none exists
 */
const getPreviousOption = (currentButton, container) => {
  const parentList = getParentOptionList(currentButton, container);
  if (!parentList) return null;

  const siblingButtons = Array.from(parentList.querySelectorAll('button')).filter(
    button => !button.disabled && button.tabIndex !== -1
  );
  const currentIndex = siblingButtons.indexOf(currentButton);
  if (currentIndex === -1) return null;

  // Loop to last if at the beginning
  const previousIndex = currentIndex === 0 ? siblingButtons.length - 1 : currentIndex - 1;
  return siblingButtons[previousIndex];
};
/**
 * Pick valid option configurations with format like:
 * [{ option, label, suboptions: [{ option, label }] }]
 *
 * @param {Array} options contain configs like { option, label, suboptions }
 * @param {Number} level specifies the nesting level
 * @returns an array of valid option configurations.
 */
const pickValidOptions = (options, level = 1) => {
  const isString = str => typeof str === 'string';
  const isValidOptions = opts => Array.isArray(opts);
  return options.reduce((picked, optionConfig) => {
    const { option, label, suboptions } = optionConfig;
    const isValid = isString(option) && isString(label);
    const suboptionsMaybe = isValidOptions(suboptions)
      ? { suboptions: pickValidOptions(suboptions, level + 1) }
      : {};
    const validOptionConfigMaybe = isValid ? [{ option, label, level, ...suboptionsMaybe }] : [];
    return [...picked, ...validOptionConfigMaybe];
  }, []);
};

const getSuboptions = optionConfig => optionConfig.suboptions;
const hasSuboptions = optionConfig => getSuboptions(optionConfig)?.length > 0;

/**
 * A component that represents a single option.
 *
 * @param {*} props include: config, level, handleChange, branchPath, containerRef
 * @returns <li> wrapped elements.
 */
const Option = props => {
  const intl = useIntl();
  const { config, level, handleChange, branchPath, ancestors = [], containerRef, ...rest } = props;
  const { option, label, suboptions } = config;
  const foundFromBranchPath = branchPath.find(bc => bc.option === option);
  const isOptSelected = !!foundFromBranchPath;
  const selectedSuboptions = branchPath.filter(bc => bc.level > foundFromBranchPath?.level);
  const isSuboptionSelected = selectedSuboptions.length > 0;
  // All options are now clickable: unselected options select, selected options deselect.
  const isClickable = true;
  const cursorMaybe = isClickable ? { cursor: 'pointer' } : { cursor: 'default' };

  const optionLabelRaw = label || option;
  const optionLabel = richText(optionLabelRaw, {
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const optionName = [...ancestors, { label: optionLabelRaw }].map(bc => bc.label).join('/');
  const pathMatch =
    isOptSelected && ancestors.length + 1 === branchPath.length ? 'exact' : 'nested';
  const ariaLabel = isOptSelected
    ? intl.formatMessage(
        { id: 'FieldSelectTree.screenreader.optionSelected' },
        { optionName, pathMatch }
      )
    : intl.formatMessage({ id: 'FieldSelectTree.screenreader.option' }, { optionName, pathMatch });

  const buttonClasses = classNames({
    [css.optionBtn]: !isOptSelected,
    [css.optionBtnSelected]: isOptSelected,
    [css.optionBtnSelectedLowest]: isOptSelected && !isSuboptionSelected,
  });

  const handleKeyDown = e => {
    if (!containerRef?.current) return;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        e.stopPropagation();
        const nextOption = getNextOption(e.target, containerRef.current);
        if (nextOption) {
          nextOption.focus();
        }
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        e.stopPropagation();
        const previousOption = getPreviousOption(e.target, containerRef.current);
        if (previousOption) {
          previousOption.focus();
        }
        break;
      }
      case 'ArrowRight': {
        if (hasSuboptions(config) && !isOptSelected) {
          e.preventDefault();
          e.stopPropagation();
          // Expand suboptions by selecting this option
          handleChange(option, level);
          // Focus will move to first child after suboptions are rendered
          setTimeout(() => {
            const allOptions = getAllFocusableOptions(containerRef.current);
            const currentIndex = allOptions.indexOf(e.target);
            if (currentIndex !== -1 && currentIndex < allOptions.length - 1) {
              allOptions[currentIndex + 1]?.focus();
            }
          }, 100);
        }
        break;
      }
      case 'ArrowLeft': {
        // Find parent button first (before DOM changes)
        const parentButton = getParentOptionButton(e.target, containerRef.current);

        // If there's a parent option (level > 1), deselect it
        if (level >= 1 && ancestors.length >= 0) {
          e.preventDefault();
          e.stopPropagation();

          if (branchPath.length > 0) {
            // Get the parent option (last ancestor)
            const parentLevel = level - 1;

            // Clear selections at parent level and below
            const updatedValue = branchPath.reduce((picked, selectedOptionByLevel) => {
              const { level: branchPathLevel, levelKey } = selectedOptionByLevel;
              if (branchPathLevel <= parentLevel && levelKey) {
                return { ...picked, [levelKey]: selectedOptionByLevel.option };
              }
              return picked;
            }, {});
            rest.onChange?.(updatedValue);
          }

          // Move focus to parent option button
          if (parentButton) {
            setTimeout(() => {
              parentButton.focus();
            }, 0);
          }
        }
        break;
      }
      case 'Home': {
        e.preventDefault();
        e.stopPropagation();
        const firstOption = getAllFocusableOptions(containerRef.current)?.[0];
        if (firstOption) {
          firstOption.focus();
        }
        break;
      }
      case 'End': {
        e.preventDefault();
        e.stopPropagation();
        const allOptions = getAllFocusableOptions(containerRef.current);
        const lastOption = allOptions?.[allOptions.length - 1];
        if (lastOption) {
          lastOption.focus();
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <li className={css.option} style={{ paddingLeft: `${12}px`, ...cursorMaybe }}>
      <button
        className={buttonClasses}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          if (isClickable) {
            handleChange(option, level);
          }
        }}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
      >
        {optionLabel}
      </button>

      {hasSuboptions(config) && isOptSelected ? (
        <SelectOptions
          options={suboptions}
          level={level + 1}
          handleChange={handleChange}
          branchPath={branchPath}
          ancestors={[...ancestors, { option, label }]}
          containerRef={containerRef}
          {...rest}
        />
      ) : null}
    </li>
  );
};

/**
 * Presentational UI component wrapping <ul>.
 *
 * @param {*} props contains properties: hasOptions & children
 * @returns its children wrapped with <ul>
 */
const OptionList = props => {
  const { hasOptions, children } = props;
  return hasOptions ? <ul className={css.optionList}>{children}</ul> : null;
};

/**
 * Component, which allows user to select one of the options. Selected option might contain suboptions.
 *
 * @param {*} props must contain property options, which is an array of option configs.
 * @returns OptionList component.
 */
const SelectOptions = props => {
  const { options, containerRef, ...rest } = props;
  return (
    <OptionList hasOptions={options?.length > 0}>
      {options.map(config => (
        <Option key={config.option} config={config} containerRef={containerRef} {...rest} />
      ))}
    </OptionList>
  );
};

// Same SVG as FieldCheckbox / FieldRadioButton (their icons are not exported); classes from their CSS modules
const IconCheckbox = () => (
  <svg width="14" height="14" xmlns="http://www.w3.org/2000/svg" role="presentation">
    <g fill="none" fillRule="evenodd">
      <g transform="translate(2 2)">
        <path
          className={checkboxCss.checked}
          d="M9.9992985 1.5048549l-.0194517 6.9993137C9.977549 9.3309651 9.3066522 10 8.4798526 10H1.5001008c-.8284271 0-1.5-.6715729-1.5-1.5l-.000121-7c0-.8284271.6715728-1.5 1.5-1.5h.000121l6.9993246.0006862c.8284272.000067 1.4999458.671694 1.499879 1.5001211a1.5002208 1.5002208 0 0 1-.0000059.0040476z"
        />
        <path
          className={classNames(checkboxCss.box, css.box)}
          strokeWidth="2"
          d="M10.9992947 1.507634l-.0194518 6.9993137C10.9760133 9.8849417 9.8578519 11 8.4798526 11H1.5001008c-1.3807119 0-2.5-1.1192881-2.5-2.4999827L-1.0000202 1.5c0-1.3807119 1.119288-2.5 2.500098-2.5l6.9994284.0006862c1.3807118.0001115 2.4999096 1.11949 2.4997981 2.5002019-.0000018.003373-.0000018.003373-.0000096.0067458z"
        />
      </g>
      <path
        d="M5.636621 10.7824771L3.3573694 8.6447948c-.4764924-.4739011-.4764924-1.2418639 0-1.7181952.4777142-.473901 1.251098-.473901 1.7288122 0l1.260291 1.1254782 2.8256927-4.5462307c.3934117-.5431636 1.1545778-.6695372 1.7055985-.278265.5473554.3912721.6731983 1.150729.2797866 1.6951077l-3.6650524 5.709111c-.2199195.306213-.5803433.5067097-.9920816.5067097-.3225487 0-.6328797-.1263736-.8637952-.3560334z"
        fill="#FFF"
      />
    </g>
  </svg>
);

const IconRadioButton = () => (
  <div>
    <svg width="14" height="14" xmlns="http://www.w3.org/2000/svg">
      <circle
        className={classNames(radioCss.notChecked, css.box)}
        cx="5"
        cy="19"
        r="6"
        transform="translate(2 -12)"
        strokeWidth="2"
        fill="none"
        fillRule="evenodd"
      />
      <g
        className={classNames(radioCss.checked, radioCss.checkedStyle)}
        transform="translate(2 -12)"
        fill="none"
        fillRule="evenodd"
      >
        <circle strokeWidth="2" cx="5" cy="19" r="6" />
        <circle fill="#FFF" fillRule="nonzero" cx="5" cy="19" r="3" />
      </g>
    </svg>
  </div>
);

// Selected ids of a level: a string for level 1, an array (or a comma-separated string) for the others
const selectedIds = value =>
  Array.isArray(value) ? value : typeof value === 'string' && value ? value.split(',') : [];

const optionText = label =>
  richText(label, { longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS, longWordClass: css.longWord });

/**
 * Category mode (D39): level 1 is a single-choice group of radios; the subcategories of the selected
 * option are checkboxes (several may be checked, OR); the next level is shown while exactly one is checked.
 * Every change computes the whole next value, so a live filter applies one consistent state:
 * another level-1 clears its predecessor's subcategories, checking a second subcategory clears the level below.
 */
const CategoryTree = props => {
  const { id, name, legend, options, value, onChange } = props;
  const levelKey = level => `${name}${level}`;
  const inputId = (level, option) => `${id}.level${level}.${option}`;

  const selectLevel1 = option => onChange({ [levelKey(1)]: option });

  // Toggle a subcategory: levels above stay, this level is rebuilt in Console order, levels below are cleared
  const toggle = (level, option, siblings) => {
    const current = selectedIds(value[levelKey(level)]);
    const toggled = current.includes(option)
      ? current.filter(o => o !== option)
      : [...current, option];
    const next = siblings.map(s => s.option).filter(o => toggled.includes(o));
    const above = [...Array(level - 1)].reduce((acc, _, i) => {
      const k = levelKey(i + 1);
      return value[k] != null ? { ...acc, [k]: value[k] } : acc;
    }, {});
    onChange(next.length > 0 ? { ...above, [levelKey(level)]: next } : above);
  };

  const renderCheckboxes = (level, parent) => {
    const checked = selectedIds(value[levelKey(level)]);
    return (
      <fieldset className={css.subgroup}>
        <legend className={css.visuallyHidden}>{parent.label}</legend>
        <ul className={css.optionList}>
          {parent.suboptions.map(config => {
            const isChecked = checked.includes(config.option);
            const showChildren =
              isChecked && checked.length === 1 && config.suboptions?.length > 0;
            const fieldId = inputId(level, config.option);
            return (
              <li key={config.option} className={css.choiceItem}>
                <span className={checkboxCss.root}>
                  <input
                    id={fieldId}
                    type="checkbox"
                    className={classNames(checkboxCss.input, css.choiceInput)}
                    name={`${id}.level${level}`}
                    value={config.option}
                    checked={isChecked}
                    onChange={() => toggle(level, config.option, parent.suboptions)}
                  />
                  <label
                    htmlFor={fieldId}
                    className={classNames(checkboxCss.label, css.choiceLabel, css.checkboxLabel)}
                  >
                    <span className={checkboxCss.checkboxWrapper}>
                      <IconCheckbox />
                    </span>
                    <span className={classNames(checkboxCss.text, checkboxCss.textRoot)}>
                      {optionText(config.label)}
                    </span>
                  </label>
                </span>
                {showChildren ? renderCheckboxes(level + 1, config) : null}
              </li>
            );
          })}
        </ul>
      </fieldset>
    );
  };

  const level1 = selectedIds(value[levelKey(1)])[0];
  // The fill marks the level that defines the results: the level-1 row is filled only while no subcategory is
  // checked; with subcategories its radio stays checked (a dot) and the checked boxes carry the selection
  const level1DefinesResults = selectedIds(value[levelKey(2)]).length === 0;
  return (
    <fieldset className={css.group}>
      <legend className={css.visuallyHidden}>{legend}</legend>
      <ul className={css.optionList}>
        {options.map(config => {
          const isChecked = level1 === config.option;
          const fieldId = inputId(1, config.option);
          return (
            <li key={config.option} className={css.choiceItem}>
              <span className={radioCss.root}>
                <input
                  id={fieldId}
                  type="radio"
                  className={classNames(radioCss.input, css.choiceInput)}
                  name={`${id}.level1`}
                  value={config.option}
                  checked={isChecked}
                  onChange={() => selectLevel1(config.option)}
                />
                <label
                  htmlFor={fieldId}
                  className={classNames(radioCss.label, css.choiceLabel, css.radioLabel, {
                    [css.radioLabelSelected]: isChecked && level1DefinesResults,
                  })}
                >
                  <span className={radioCss.radioButtonWrapper}>
                    <IconRadioButton />
                  </span>
                  <span className={radioCss.text}>{optionText(config.label)}</span>
                </label>
              </span>
              {isChecked && config.suboptions?.length > 0 ? renderCheckboxes(2, config) : null}
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
};

/**
 * Returns an array of selected nested option configurations.
 * It roughly looks like this:
 * [
 *   { option: 'women', label: 'Women', levelKey: 'categoryLevel1', suboptions: [etc.] },
 *   { option: 'jackets', label: 'Jackets', levelKey: 'categoryLevel2' },
 * ]
 *
 * @param {Array} primaryOptions highest level of options
 * @param {Object} currentSelections selected nested options: { categoryLevel1: 'women', categoryLevel2: 'jacket' }
 * @returns an array of selected nested option configurations.
 */
const getBranchPath = (primaryOptions, currentSelections) => {
  const currentSelectionsEntries = Object.entries(currentSelections).sort(([k, v]) => k);
  return currentSelectionsEntries.reduce((branchPath, entry) => {
    const [levelKey, selectedOption] = entry;

    const currentOptions =
      branchPath.length > 0 ? branchPath[branchPath.length - 1].suboptions : primaryOptions;
    const foundOption = currentOptions.find(o => o.option === selectedOption);
    const foundOptionWithLevelKeyMaybe = foundOption ? [{ ...foundOption, levelKey }] : [];

    return [...branchPath, ...foundOptionWithLevelKeyMaybe];
  }, []);
};

/**
 * Handle value changes that happen when user clicks different options.
 *
 * @param {Array} primaryOptions highest level of options
 * @param {Object} currentSelections selected nested options: { categoryLevel1: 'women', categoryLevel2: 'jacket' }
 * @param {String} namePrefix like "categoryLevel"
 * @param {Function} onChange Final Form's onChange function.
 */
const handleChangeFn = (primaryOptions, currentSelections, namePrefix, onChange) => (
  currentOption,
  level
) => {
  const branchPath = getBranchPath(primaryOptions, currentSelections);

  // Deselect: if the clicked option is already selected at this level,
  // keep only ancestor levels (levels strictly below the clicked level)
  // and clear the clicked level and all deeper levels.
  const levelKeyToCheck = `${namePrefix}${level}`;
  if (currentSelections[levelKeyToCheck] === currentOption) {
    const ancestorValue = branchPath.reduce((acc, bc) => {
      return bc.level < level ? { ...acc, [bc.levelKey]: bc.option } : acc;
    }, {});
    onChange(ancestorValue);
    return;
  }

  const updatedCurrentBranchPath = branchPath.reduce((picked, selectedOptionByLevel) => {
    const { level: branchPathLevel, option: optionByLevel } = selectedOptionByLevel;
    const levelKey = `${namePrefix}${branchPathLevel}`;

    return branchPathLevel < level
      ? { ...picked, [levelKey]: optionByLevel }
      : branchPathLevel === level
      ? { ...picked, [levelKey]: currentOption }
      : picked;
  }, {});

  const levelKeyToUpdate = `${namePrefix}${level}`;
  const isUpdateANewBranchPath = updatedCurrentBranchPath[levelKeyToUpdate] == null;
  const updatedValue = isUpdateANewBranchPath
    ? { ...updatedCurrentBranchPath, [levelKeyToUpdate]: currentOption }
    : updatedCurrentBranchPath;

  onChange(updatedValue);
};

/**
 * @typedef {Object} SelectTreeOptionConfig
 * @property {string} option
 * @property {string} label
 * @property {Array<SelectTreeOptionConfig>} suboptions
 */

/**
 * Create Final Form field that represents "tree select" component,
 * where user can select nested options like categories.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.name Name of the input in Final Form
 * @param {ReactNode} props.label
 * @param {Array<SelectTreeOptionConfig>} props.options
 * @param {boolean} [props.multiSelectSublevels] category mode (D39): level-1 radios, subcategory checkboxes
 * @param {string} [props.id] id prefix of the inputs (category mode)
 * @param {ReactNode} [props.legend] name of the level-1 group (category mode)
 * @returns {JSX.Element} Final Form Field containing nested "select" input
 */
const FieldSelectTree = props => {
  const {
    className,
    rootClassName,
    label,
    name,
    options,
    multiSelectSublevels = false,
    id,
    legend,
    ...rest
  } = props;
  const namePrefix = name;
  const level = 1;
  const validOptions = pickValidOptions(options);
  const containerRef = useRef(null);

  const classes = classNames(rootClassName || css.root, className);

  return (
    <Field name={name} {...rest}>
      {fieldProps => {
        const { value, onChange } = fieldProps?.input || {};
        const fieldValue = value ? value : {};
        const meta = fieldProps?.meta;

        if (multiSelectSublevels) {
          return (
            <div className={classes}>
              <CategoryTree
                id={id || name}
                name={namePrefix}
                legend={legend}
                options={validOptions}
                value={fieldValue}
                onChange={onChange}
              />
              <ValidationError fieldMeta={meta} />
            </div>
          );
        }

        const branchPath = getBranchPath(validOptions, fieldValue);

        return (
          <div ref={containerRef} className={classes}>
            {label ? <label>{label}</label> : null}
            <SelectOptions
              options={validOptions}
              level={level}
              handleChange={handleChangeFn(validOptions, fieldValue, namePrefix, onChange)}
              branchPath={branchPath}
              containerRef={containerRef}
              namePrefix={namePrefix}
              onChange={onChange}
            />

            <ValidationError fieldMeta={meta} />
          </div>
        );
      }}
    </Field>
  );
};

export default FieldSelectTree;
